import { MdSend } from '@react-icons/all-files/md/MdSend'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Link,
  Location,
  Outlet,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import CloseSvg from '!@svgr/webpack!assets/icons/close.svg'

import { EMAIL_CAMPAIGN_BUILDER } from '@disco/constants/FeatureTypes'
import {
  loadContactEmailGroups,
  loadEmailGroup,
  loadEmailGroups,
  loadMailingLists,
} from '@disco/state-management/actions/EmailGroupActions'
import { searchContacts } from '@disco/state-management/actions/contact-actions'
import { updateUserSettingsUnsafe } from '@disco/state-management/actions/ExplorerActions'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getEmailGroup } from '@disco/state-management/selectors/emailGroups'

import DocumentHeadTags from 'components/document-head-tags'
import { Spinner } from 'components/ui'
import ContactUsLink from 'components/ui/contact-us-link'

import type { RootState } from 'store/configureStore'

import ListDialog from './list-dialog'
import styles from './index.module.css'

type OwnProps = {
  children: React.ReactNode
  location: Location
  params: {
    listId?: string
    contactId?: string
  }
  searchParams: URLSearchParams
}

function mapStateToProps(state: RootState, props: OwnProps) {
  let listId
  let list = null

  if (props.params.listId) {
    listId = parseInt(props.params.listId)
  } else if (props.searchParams.get('append')) {
    listId = parseInt(props.searchParams.get('append'), 10)
  }

  if (listId) {
    list = getEmailGroup(state, {
      listId: listId,
    })
    list = list || {
      id: listId,
      loading: true,
      name: '',
    }
  }

  return {
    settings: state.user.settings,
    emailGroups: state.emailGroups,
    list,
    campaignsEnabled: isFeatureEnabled(state, {
      featureName: EMAIL_CAMPAIGN_BUILDER,
    }),
  }
}

const mapDispatchToProps = {
  loadEmailGroups,
  loadEmailGroup,
  updateUserSettingsUnsafe,
  loadContactEmailGroups,
  loadMailingLists,
  searchContacts,
}

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type
type PropsFromRedux = ConnectedProps<typeof connector>

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps
class Contacts extends React.Component<Props> {
  componentDidMount() {
    const contactId = this.props.params?.contactId ?? this.props.list?.id
    const selectedContactId =
      contactId != null
        ? typeof contactId === 'string'
          ? parseInt(contactId, 10)
          : contactId
        : null

    if (selectedContactId || (!this.props.emailGroups && selectedContactId))
      this.props.loadContactEmailGroups(selectedContactId)
    this.props.loadMailingLists()
    this.props.loadEmailGroups()
  }
  componentDidUpdate(prevProps: Props) {
    const prevListId = parseInt(prevProps.params?.listId?.toString(), 10)
    const currentListId = parseInt(this.props.params?.listId?.toString(), 10)
    const { emailGroups, mailingList } = this.props.emailGroups

    const prevEmailGroups = prevProps.emailGroups.emailGroups

    const prevRecipients = prevEmailGroups?.flatMap(group => group?.recipients)
    const currentRecipients =
      mailingList && emailGroups?.flatMap(group => group?.recipients)

    const isGroupUpdating = isEqual(prevRecipients, currentRecipients)
    if (currentListId && prevListId !== currentListId && isGroupUpdating) {
      this.props.loadEmailGroup(currentListId)
    }
  }

  componentWillUnmount() {
    // this wrapper stays mounted for all in-section contacts/lists
    // navigation (switching lists, viewing a contact and back), so
    // unmounting means the user actually left the section - clear the
    // search rather than let it resurface on an unrelated later visit
    this.props.searchContacts('', null)
  }

  _handleCloseUpgrade = () => {
    this.props.updateUserSettingsUnsafe({
      settings: {
        dismiss_email_creator_upgrade_2019_02: true,
      },
    })
  }

  render() {
    const { list, campaignsEnabled } = this.props
    let showUpgrade = !campaignsEnabled

    if ((this.props.settings as any).dismiss_email_creator_upgrade_2019_02) {
      showUpgrade = false
    }

    return (
      <div className="Page">
        <DocumentHeadTags title="Contacts | DISCO" />
        <ListDialog />
        {showUpgrade && (
          <div className={styles.contactsBanner}>
            <MdSend className={styles.contactsBannerIcon} />
            Want a better way to email your contacts?&nbsp;
            <ContactUsLink
              className={styles.contactsBannerLink}
              intercomMsg="I'd like to discuss upgrading to access the Email Creator"
            >
              Upgrade
            </ContactUsLink>
            &nbsp;to access our&nbsp;
            <Link to="/campaigns" className={styles.contactsBannerLink}>
              Email creator
            </Link>
            <button
              className={styles.contactsBannerClose}
              onClick={this._handleCloseUpgrade}
            >
              <CloseSvg />
            </button>
            .
          </div>
        )}
        <div className="Page_Content">
          {list && list.loading ? <Spinner /> : <Outlet />}
        </div>
      </div>
    )
  }
}

const ConnectedContacts = connector(Contacts)

// Wrapper component to provide router params to the connected component
const ContactsWithRouter = (props: any) => {
  const params = useParams<{ listId?: string }>()
  const [searchParams] = useSearchParams()

  return (
    <ConnectedContacts {...props} params={params} searchParams={searchParams} />
  )
}

export default ContactsWithRouter
